import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, ShoppingBag, Plus, Minus, Trash2, ArrowRight, Store, Bike } from 'lucide-react';
import { useRestaurantCart } from '@happyspace/sdk/restaurant';
import { useCartUI } from '../lib/cartUi';
import { money } from '../lib/util';
import { SITE } from '../data/site';

export function CartDrawer() {
  const { isCartOpen, closeCart } = useCartUI();
  const { items, subtotal, total, updateQuantity, removeItem, itemCount, orderType, setOrderType } = useRestaurantCart();
  const navigate = useNavigate();

  useEffect(() => {
    document.body.style.overflow = isCartOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [isCartOpen]);

  const handleCheckout = () => {
    closeCart();
    navigate('/checkout');
  };

  const lineTotal = (item: any) => (item.basePrice + (item.modifierTotal || 0)) * item.quantity;

  return (
    <>
      <div
        onClick={closeCart}
        className={`fixed inset-0 z-[120] transition-opacity duration-300 ${isCartOpen ? 'opacity-100' : 'pointer-events-none opacity-0'}`}
        style={{ background: 'var(--pe-scrim)', backdropFilter: 'blur(4px)' }}
      />
      <aside
        role="dialog"
        aria-label="Your order"
        className="fixed top-0 right-0 h-full w-full sm:w-[26rem] z-[130] flex flex-col transition-transform duration-400"
        style={{ background: 'var(--color-surface)', transform: isCartOpen ? 'translateX(0)' : 'translateX(100%)', boxShadow: 'var(--pe-shadow-lg)' }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-5" style={{ borderBottom: '1px solid var(--color-line)' }}>
          <div className="flex items-center gap-2.5">
            <ShoppingBag size={20} style={{ color: 'var(--color-accent)' }} />
            <h2 className="font-display text-xl text-ink">Your Order</h2>
            {itemCount > 0 && (
              <span className="tnum text-xs font-bold px-2 py-0.5 rounded-full" style={{ background: 'var(--color-accent)', color: 'var(--color-on-accent)' }}>{itemCount}</span>
            )}
          </div>
          <button onClick={closeCart} aria-label="Close cart" className="grid place-items-center w-9 h-9 rounded-full transition-transform active:scale-90" style={{ background: 'var(--color-card-2)' }}>
            <X size={16} />
          </button>
        </div>

        {/* Items */}
        <div className="flex-1 overflow-y-auto px-6 py-5">
          {(!items || items.length === 0) ? (
            <div className="flex flex-col items-center justify-center h-full gap-4 text-center">
              <div className="grid place-items-center w-20 h-20 rounded-full" style={{ background: 'var(--color-card-2)' }}>
                <ShoppingBag size={34} style={{ color: 'var(--color-faint)' }} />
              </div>
              <div>
                <p className="font-semibold text-ink">Your cart is empty</p>
                <p className="text-sm text-muted mt-1">Add dishes from the menu to get started.</p>
              </div>
              <button onClick={() => { closeCart(); navigate('/menu'); }} className="btn btn-primary" style={{ padding: '0.6rem 1.4rem' }}>Browse menu</button>
            </div>
          ) : (
            <ul className="space-y-3">
              {items.map((item: any) => (
                <li key={item.cartItemKey} className="flex gap-3 p-3 rounded-2xl" style={{ background: 'var(--color-card)', border: '1px solid var(--color-line)' }}>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-ink text-[0.95rem] leading-tight">{item.title}</p>
                    {item.selectedModifiers && item.selectedModifiers.length > 0 && (
                      <p className="text-xs text-muted mt-0.5">{item.selectedModifiers.map((m: any) => m.name).join(', ')}</p>
                    )}
                    <p className="tnum font-display text-[0.95rem] mt-1" style={{ color: 'var(--color-accent-deep)' }}>{money(lineTotal(item))}</p>
                  </div>
                  <div className="flex flex-col items-end justify-between">
                    <button onClick={() => removeItem(item.cartItemKey)} aria-label="Remove" className="text-muted hover:text-[var(--color-spice)] transition-colors">
                      <Trash2 size={16} />
                    </button>
                    <div className="flex items-center gap-2">
                      <button onClick={() => updateQuantity(item.cartItemKey, item.quantity - 1)} aria-label="Decrease" className="grid place-items-center w-7 h-7 rounded-full transition-colors" style={{ border: '1px solid var(--color-line-2)' }}>
                        <Minus size={13} />
                      </button>
                      <span className="tnum text-sm font-bold w-4 text-center">{item.quantity}</span>
                      <button onClick={() => updateQuantity(item.cartItemKey, item.quantity + 1)} aria-label="Increase" className="grid place-items-center w-7 h-7 rounded-full transition-colors" style={{ border: '1px solid var(--color-line-2)' }}>
                        <Plus size={13} />
                      </button>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Footer */}
        {items && items.length > 0 && (
          <div className="px-6 py-5 space-y-3" style={{ borderTop: '1px solid var(--color-line)' }}>
            <div className="grid grid-cols-2 gap-2 p-1 rounded-full" style={{ background: 'var(--color-card-2)' }}>
              <button
                type="button"
                onClick={() => setOrderType('pickup')}
                className="flex items-center justify-center gap-1.5 py-2 rounded-full text-sm font-semibold transition-all"
                style={orderType !== 'delivery' ? { background: 'var(--color-accent)', color: 'var(--color-on-accent)' } : { color: 'var(--color-ink-2)' }}
              >
                <Store size={14} /> Pickup
              </button>
              <button
                type="button"
                onClick={() => setOrderType('delivery')}
                className="flex items-center justify-center gap-1.5 py-2 rounded-full text-sm font-semibold transition-all"
                style={orderType === 'delivery' ? { background: 'var(--color-accent)', color: 'var(--color-on-accent)' } : { color: 'var(--color-ink-2)' }}
              >
                <Bike size={14} /> Delivery
              </button>
            </div>
            <div className="flex justify-between text-sm text-ink-2">
              <span>Subtotal</span>
              <span className="tnum">{money(subtotal)}</span>
            </div>
            <div className="flex justify-between font-display text-lg text-ink">
              <span>Total</span>
              <span className="tnum" style={{ color: 'var(--color-accent-deep)' }}>{money(total)}</span>
            </div>
            <button onClick={handleCheckout} className="btn btn-primary w-full justify-center">
              Proceed to checkout <ArrowRight size={16} />
            </button>
            <p className="text-center text-[0.72rem] text-muted">
              {orderType === 'delivery' ? 'Delivery fee & ETA calculated at checkout' : `Pickup at ${SITE.address.line1}, ${SITE.city}`}
            </p>
          </div>
        )}
      </aside>
    </>
  );
}
